// ===============================
// UC Menu Items V2 API Communication
// ===============================

import { useCallback, useMemo, useRef, useState } from 'react';

import {
	BFFError,
	BFFNetworkError,
	useNavBoundaryContext,
} from '@atlassian/navigation-error-boundary';
import { createExperienceTracker } from '@atlassian/navigation-perf-utils';
import type { UniversalCreateSourceProduct } from '@atlassian/universal-create-common';
import {
	type AppMenuConfig,
	encodeConfig,
	type PersonalizeMode,
} from '@atlassian/universal-create-common/bin-param';

import { RequestStatus, UC_MENU_ITEMS_REQUEST_STATUS_KEY } from '../../constants';
import { useBFFContext } from '../context';
import { type InFlightRequestValue, useUniversalCreateBFFContext } from '../universalCreateContext';
import { fetchJSON } from '../utils/request';

import { useGenericCache } from './useGenericCache';
import { useStatus } from './useStatus';
import { useURLBuilder } from './useURLBuilder';

// ===============================
// Types
// ===============================

export interface SuggestionItemResponseBase {
	/**
	 * The ATI of the object type
	 */
	ati: string;
	/**
	 * The icon name from @atlaskit/icon/core/{name}
	 */
	icon?: string;
	/**
	 * The name of the object type
	 */
	name: string;
	/**
	 * The product name where the object type belongs to
	 */
	product: UniversalCreateSourceProduct;
}

export interface IntegrationItemResponse extends SuggestionItemResponseBase {
	actionType: string;
	type: 'integration';
}

export interface IframeItemResponse extends SuggestionItemResponseBase {
	src: string;
	type: 'iframe';
}

export interface LinkItemResponse extends SuggestionItemResponseBase {
	hostname: string;
	href: string;
	options?: Record<string, string | number | boolean>;
	sessionSyncUrl?: string;
	target: '_self' | '_blank';
	type: 'link';
}

export interface PlatformItemResponse extends SuggestionItemResponseBase {
	type: 'platform';
}

export type SuggestionItemResponse =
	| IntegrationItemResponse
	| IframeItemResponse
	| LinkItemResponse
	| PlatformItemResponse;

export interface SuggestionsAPIResponse {
	isPartial?: boolean;
	items: SuggestionItemResponse[];
}

/**
 * Native menu item returned as-is from the request
 * @private
 */
export interface NativeMenuItemResponse {
	ati: string;
	promoted?: boolean;
	type: 'native';
}

/**
 * Response item for cross-app items
 * @private
 */
export type CrossAppItemResponse = SuggestionItemResponse;

/**
 * Union type for all processed menu items
 * @private
 */
export type ProcessedMenuItem = NativeMenuItemResponse | CrossAppItemResponse;

/**
 * Processed suggestion section in the response
 * @private
 */
export interface ProcessedSuggestionSection {
	items: CrossAppItemResponse[];
	limit: number;
	type: 'suggestionSection';
}

/**
 * Processed app-defined section in the response
 * @private
 */
export interface ProcessedAppDefinedSection {
	items: ProcessedMenuItem[];
	personalized: PersonalizeMode;
	type: 'appDefinedSection';
}

/**
 * Union type for processed sections
 * @private
 */
export type ProcessedMenuSection = ProcessedSuggestionSection | ProcessedAppDefinedSection;

/**
 * Complete V2 menu items API response
 * @private
 */
export interface MenuItemsAPIResponse {
	growthRecommendationsSessionId?: string;
	isPartial?: boolean;
	sections: ProcessedMenuSection[];
}

/**
 * Props for useUCMenuItems hook
 * @private
 */
interface UseUCMenuItemsProps {
	appMenuConfig: AppMenuConfig;
	cloudId: string;
	locale?: string;
	orgId: string;
	product: string;
	skipInitialization?: boolean;
	subProduct?: string;
	userId?: string;
}

/**
 * Return type for useUCMenuItems hook
 * @private
 */
export interface UseUCMenuItemsReturn {
	data: MenuItemsAPIResponse | undefined;
	fetchUCMenuItems: () => Promise<MenuItemsAPIResponse | undefined>;
	hasError: boolean;
	isLoading: boolean;
	status: RequestStatus | null;
}

/**
 * Return type for useUCSuggestions hook
 * @private
 */
export interface UseUCSuggestionsReturn {
	data: SuggestionsAPIResponse | undefined;
	fetchUCSuggestions: () => Promise<SuggestionsAPIResponse | undefined>;
	hasError: boolean;
	isLoading: boolean;
	status: RequestStatus | null;
}

// ===============================
// Hook Implementation
// ===============================

/**
 * Hook for fetching UC menu items V2 with smart caching and binary encoding
 *
 * @private Use in Universal Create only
 * This hook:
 * - Encodes the appMenuConfig using binary encoding (d, ro, rt, ap params)
 * - Fetches menu items from the V2 endpoint
 * - Handles caching, loading states, and errors
 * - Follows the same patterns as useUCSuggestions
 */
export function useUCMenuItems({
	cloudId,
	orgId,
	product,
	userId,
	locale,
	subProduct,
	appMenuConfig,
	skipInitialization = false,
}: UseUCMenuItemsProps): UseUCMenuItemsReturn {
	const { value, currentLoadingRequests } = useBFFContext();
	const boundaryConfig = useMemo(
		() => ({
			location: 'useUCMenuItems' as const,
			disableSentry: false,
			disableAnalytics: true,
			sentryConfig: { product },
			interactionId: 'universalCreatePrefetch' as const,
			tags: {
				component: 'universal-create' as const,
				subProduct: subProduct,
			},
		}),
		[product, subProduct],
	);
	const { handleError } = useNavBoundaryContext(boundaryConfig);
	const { inFlightRequests, setInFlightRequests } = useUniversalCreateBFFContext();
	const { setStatus } = useStatus();
	const [data, setData] = useState<MenuItemsAPIResponse | undefined>(undefined);
	const [hasError, setHasError] = useState(false);
	const { buildUCMenuItemsURL } = useURLBuilder({ cloudId });

	const requestIdRef = useRef(0);

	/**
	 * Helper functions for feature gated inflight request tracking
	 */
	const isRequestInflight = useCallback(
		(requestKey: string): boolean => {
			return inFlightRequests.has(requestKey);
		},
		[inFlightRequests],
	);

	const setRequestInFlight = useCallback(
		(requestKey: string, value: InFlightRequestValue) => {
			return setInFlightRequests(requestKey, value);
		},
		[setInFlightRequests],
	);

	const clearInFlightRequest = useCallback(
		(requestKey: string) => {
			return inFlightRequests.delete(requestKey);
		},
		[inFlightRequests],
	);

	// Encode the config to get binary parameters
	const encodedConfig = useMemo(() => {
		if (skipInitialization) {
			return null;
		}

		try {
			return encodeConfig(appMenuConfig);
		} catch {
			// If encoding fails, we'll handle it in the fetch
			return null;
		}
	}, [appMenuConfig, skipInitialization]);

	// Generate cache key based on encoded config and metadata
	const { cachedObject, setCachedObject } = useGenericCache<MenuItemsAPIResponse>({
		cloudId,
		productType: product,
		orgId,
		accountId: userId,
		subProduct,
		locale,
		// Include encoded config in cache key to ensure different configs get different cache entries
		...(encodedConfig && { encodedConfigD: encodedConfig.d }),
	});

	// Set data from cache on mount if available
	if (cachedObject && !data) {
		setData(cachedObject);
		setHasError(false);
	}

	const fetchUCMenuItems = useCallback(async (): Promise<MenuItemsAPIResponse | undefined> => {
		// Early exit: filter out invalid param requests when required identifiers are missing or skip initialization if requested
		if (!cloudId || !orgId || skipInitialization) {
			return;
		}

		// Early exit: if encoding failed
		if (!encodedConfig) {
			setHasError(true);
			setStatus(RequestStatus.ERROR, UC_MENU_ITEMS_REQUEST_STATUS_KEY);
			return;
		}

		// Return cached data if available (already locale-scoped via cache key)
		if (cachedObject) {
			setStatus(RequestStatus.COMPLETE, UC_MENU_ITEMS_REQUEST_STATUS_KEY);
			return cachedObject;
		}

		// Generate request key to prevent duplicate concurrent requests
		const requestKey = `${product}|${orgId}|${userId}|${locale}|${subProduct}|${encodedConfig.d}`;

		// Skip if an identical request is already in flight
		if (isRequestInflight(requestKey)) {
			return;
		}

		// Increment request id for race protection
		requestIdRef.current += 1;
		const scopedId = requestIdRef.current;

		// Create and start experience tracker only when actually making request
		const experience = createExperienceTracker('universalCreatePrefetch');

		experience?.start();

		try {
			const url = buildUCMenuItemsURL({
				product,
				orgId,
				userId,
				locale,
				subProduct,
				d: encodedConfig.d,
				ro: encodedConfig.ro,
				rt: encodedConfig.rt,
				ap: encodedConfig.ap,
			});

			setStatus(RequestStatus.LOADING, UC_MENU_ITEMS_REQUEST_STATUS_KEY);

			// Mark request key as in-flight before fetch fires to prevent race conditions
			setRequestInFlight(requestKey, true);

			const response = await fetchJSON<MenuItemsAPIResponse>(url, {});

			// Ignore if this response is for a stale request
			if (scopedId !== requestIdRef.current) {
				// Clear in-flight key if this was the request that set it
				if (isRequestInflight(requestKey)) {
					clearInFlightRequest(requestKey);
				}

				return;
			}

			if (response.status === RequestStatus.ERROR) {
				experience?.failure();
				setHasError(true);
				setData(undefined);
				setStatus(RequestStatus.ERROR, UC_MENU_ITEMS_REQUEST_STATUS_KEY);

				// Clear in-flight key on error
				clearInFlightRequest(requestKey);

				handleError(
					new BFFNetworkError('Failed to fetch universal create menu items', {
						url,
						status: response.status,
						originalError: response.error,
						additionalContext: {
							requestKey,
						},
					}),
				);
				return;
			}

			if (response.status === RequestStatus.COMPLETE && response.data) {
				experience?.success();
				setData(response.data);
				setHasError(false);

				// Only cache if the data is not partial
				if (!response.data.isPartial) {
					setCachedObject(response.data);
				}

				setStatus(RequestStatus.COMPLETE, UC_MENU_ITEMS_REQUEST_STATUS_KEY);

				// Clear in-flight key on success
				clearInFlightRequest(requestKey);

				return response.data;
			}
		} catch (error) {
			experience?.failure();

			if (scopedId === requestIdRef.current) {
				setHasError(true);
				setData(undefined);
				setStatus(RequestStatus.ERROR, UC_MENU_ITEMS_REQUEST_STATUS_KEY);
			}

			// Clear in-flight key on exception
			clearInFlightRequest(requestKey);

			handleError(
				new BFFError('Error occurred during fetchUCMenuItems', {
					status: RequestStatus.ERROR,
					originalError: error,
					additionalContext: {
						requestKey,
					},
				}),
			);
		}

		return;
	}, [
		cloudId,
		orgId,
		skipInitialization,
		encodedConfig,
		cachedObject,
		product,
		userId,
		locale,
		subProduct,
		isRequestInflight,
		setStatus,
		buildUCMenuItemsURL,
		setRequestInFlight,
		clearInFlightRequest,
		handleError,
		setCachedObject,
	]);

	const isLoading = useMemo(() => {
		if (skipInitialization) {
			return false;
		}

		// We check if the current loading requests include UC menu items rather than the BFF status directly
		// to ensure that it only returns true for UC menu items and not other BFF requests.
		const isBffLoadingOurStuff =
			currentLoadingRequests?.includes(UC_MENU_ITEMS_REQUEST_STATUS_KEY) ?? false;

		// That said, if the BFF is in loading state but data has been returned, we want to wait for things to complete
		// before running things like onResultChange, so that we don't do those things an unnecessary extra time
		// during the brief moment when data is present but loading is still true, or an error has occurred and loading is still true.
		const hasMenuItemDataBeenReturned = data !== undefined || hasError;

		return isBffLoadingOurStuff && !hasMenuItemDataBeenReturned;
	}, [currentLoadingRequests, data, hasError, skipInitialization]);

	return {
		data,
		isLoading,
		hasError,
		status: value.status,
		fetchUCMenuItems,
	};
}
